import {
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Popper,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useEffect, useRef, useState } from "react";
import { DataGrid, GridColDef, GridRowsProp } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ColumnSettings from "./column-settings";
import ErrorNotification from "../../../../../../components/error-notification/error-notification";
import ConfirmationDialog from "../../../../../../components/confirmation-dialog/confirmation-dialog";

type ColumnSettingsType = {
  label: string;
  inputType: "OpenEnd" | "SingleSelect" | "TextField";
  inputFormat: string;
  isSum: boolean;
  column: number;
  answerOptions?: string;
  width?: number;
};

export default function ConfigureTable({
  definedNames,
  usedDefinedNames,
  tableData,
  showDialog,
  closeDialog,
  onSave,
}: any) {
  const [tableWidth, setTableWidth] = useState<number>();
  const [columns, setColumns] = useState<GridColDef[]>([]);
  const [rows, setRows] = useState<GridRowsProp>([]);
  const [selectedColumn, setSelectedColumn] = useState<any>();
  const [isEdit, setIsEdit] = useState(false);
  const [showErrorNotification, setShowErrorNotification] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showAlertDialog, setShowAlertDialog] = useState(false);
  const [allColumnsWidth, setAllColumnsWidth] = useState(0);
  const [selectedDefinedNames, setSelectedDefinedNames] = useState<string[]>(
    []
  );
  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!tableWidth) {
      setTableWidth(tableRef?.current?.getBoundingClientRect().width);
    }
  }, [tableRef?.current?.getBoundingClientRect().width]); // eslint-disable-line

  useEffect(() => {
    if (tableData.length > 0 && tableWidth) {
      const filterHeader = tableData
        ?.filter((element: any) => element?.tableElementSettings?.isHeader)
        .sort(
          (a: any, b: any) =>
            a?.tableElementSettings?.column - b?.tableElementSettings?.column
        );
      const filterNonHeader = tableData?.filter(
        (element: any) => element?.tableElementSettings?.isHeader === false
      );

      const filterAutoWidthLength = filterHeader.filter(
        (col: any) => col.tableElementSettings.headerSettings.width === 0
      ).length;
      const filterNonAutoWidth = filterHeader.filter(
        (col: any) => col.tableElementSettings.headerSettings.width !== 0
      );
      let nonAutoWidthSum = 0;
      filterNonAutoWidth.forEach((col: any) => {
        nonAutoWidthSum += col.tableElementSettings.headerSettings.width;
      });
      const tableWidth = tableRef?.current?.getBoundingClientRect().width;

      const tableColumns: any[] = [];
      filterHeader.forEach((header: any, index: number) => {
        let width;
        if (tableWidth) {
          if (header?.tableElementSettings?.headerSettings?.width === 0) {
            width =
              (12 / filterAutoWidthLength / 12) *
              (tableWidth - (nonAutoWidthSum / 12) * tableWidth);
          } else {
            width =
              (header?.tableElementSettings?.headerSettings?.width / 12) *
              tableWidth;
          }
        }
        const defaultSettings: ColumnSettingsType = {
          label: header?.title,
          inputType: header?.tableElementSettings?.headerSettings?.inputType,
          inputFormat:
            header?.tableElementSettings?.headerSettings?.inputFormat,
          column: header?.tableElementSettings?.column,
          isSum: header?.tableElementSettings?.headerSettings?.calculateSum,
          answerOptions: header?.answerOptions
            ? header?.answerOptions.join(", ")
            : "",
          width: header?.tableElementSettings?.headerSettings?.width,
        };
        const column: GridColDef & { settings: ColumnSettingsType } = {
          field: `col${index + 1}`,
          headerName: header?.title,
          width: width,
          type:
            defaultSettings.inputType === "TextField"
              ? "string"
              : "singleSelect",
          // valueOptions: header?.answerOptions,
          renderHeader: (params) => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                width: params.colDef.width,
              }}
            >
              <Typography
                sx={{
                  width: params?.colDef?.width
                    ? params?.colDef?.width - 50
                    : 100,
                  fontWeight: "600",
                }}
                noWrap
              >
                {params.colDef.headerName}
              </Typography>
              <IconButton onClick={() => onEditHeaderClick(params)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Box>
          ),
          renderCell: (params) => {
            if (params.id !== "sum") {
              if (params?.colDef?.type === "string") {
                return (
                  <TextField
                    fullWidth
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        "& fieldset": {
                          border: "none",
                        },
                        "&:hover fieldset": {
                          border: "none",
                        },
                        "&.Mui-focused fieldset": {
                          border: "none",
                        },
                        "& .MuiOutlinedInput-input": {
                          padding: "13px 2px",
                        },
                      },
                    }}
                    value={params?.value || ""}
                    onKeyDown={(e) => {
                      if (e.key === " ") {
                        e.stopPropagation();
                      }
                    }}
                    onChange={(event) => {
                      handleTextChange(
                        params.id as number,
                        params.field,
                        event.target.value
                      );
                    }}
                  />
                );
              }

              return (
                <Autocomplete
                  fullWidth
                  sx={{
                    width: "100%",
                    height: 40,
                    my: 0.75,
                    "& .MuiOutlinedInput-root": {
                      padding: 0,
                    },
                  }}
                  disablePortal
                  options={definedNames}
                  getOptionDisabled={(option) =>
                    selectedDefinedNames.includes(option)
                  }
                  value={params.value ? `#${params.value}` : ""}
                  onChange={(event, newValue: any) => {
                    handleSelectChange(
                      params.id as number,
                      params.field,
                      newValue,
                      params.value
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      sx={{
                        "& .MuiOutlinedInput-input": {
                          color: "#2E7D32",
                        },
                        "& .MuiOutlinedInput-root": {
                          paddingRight: "44px !important",
                        },
                        "& .MuiOutlinedInput-root .MuiAutocomplete-endAdornment":
                          {
                            right: "0px !important",
                          },
                      }}
                      name="definedName"
                      {...params}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li
                      {...props}
                      style={{
                        width: 300,
                        whiteSpace: "normal",
                        overflowWrap: "break-word",
                        wordBreak: "break-all",
                      }}
                    >
                      {option}
                    </li>
                  )}
                  slots={{ popper: Popper }}
                  slotProps={{
                    popper: {
                      disablePortal: false,
                      sx: {
                        zIndex: 1500,
                        width: "auto",
                      },
                    },
                    paper: {
                      sx: {
                        width: 300,
                        backgroundColor: "#fafafa",
                      },
                    },
                  }}
                />
              );
            }
          },
          settings: defaultSettings,
        };
        tableColumns.push(column);
      });
      setColumns(tableColumns);

      const rows: any[] = [];
      const numberOfRows = filterNonHeader.length / filterHeader.length;
      const addedDefinedName: string[] = [];
      for (let i = 1; i <= numberOfRows; i++) {
        const filterRow = tableData
          .filter((element: any) => element?.tableElementSettings?.row === i)
          .sort(
            (a: any, b: any) =>
              a?.tableElementSettings?.column - b?.tableElementSettings?.column
          );
        const updateRow: any = { id: i };
        filterRow.forEach((row: any, index: number) => {
          const filterColumn: any = tableColumns.find(
            (col) => col.field === `col${index + 1}`
          );
          if (filterColumn?.settings?.inputType === "TextField") {
            updateRow[`col${index + 1}`] = row?.title;
          } else {
            updateRow[`col${index + 1}`] = row?.definedName;
            addedDefinedName.push(row?.definedName);
          }
        });
        rows.push(updateRow);
      }
      setRows(rows);
    }
  }, [tableData, tableWidth]); // eslint-disable-line

  useEffect(() => {
    let totalWidth = 0;
    columns.forEach((col: any) => {
      totalWidth += col?.settings?.width === 0 ? 1 : col?.settings?.width;
    });
    setAllColumnsWidth(totalWidth);
  }, [columns]);

  useEffect(() => {
    if (usedDefinedNames && usedDefinedNames.length > 0) {
      const definedNames: string[] = [];
      definedNames.push(...usedDefinedNames);
      setSelectedDefinedNames(definedNames);
    }
  }, [usedDefinedNames]);

  const updateColumnWidth = (newColumn?: any) => {
    let isColumnAvailable = false;
    columns.forEach((col: any, i: number) => {
      if (i === newColumn.settings.column) {
        isColumnAvailable = true;
        col.headerName = newColumn.headerName;
        col.type =
          newColumn.settings.inputType === "TextField"
            ? "string"
            : "singleSelect";
        col.settings = { ...newColumn.settings };
      }
    });
    const updateColumns = isColumnAvailable
      ? [...columns]
      : [...columns, newColumn];

    if (updateColumns.length > 0) {
      const filterAutoWidthLength = updateColumns.filter(
        (col: any) => col.settings.width === 0
      ).length;
      const filterNonAutoWidth = updateColumns.filter(
        (col: any) => col.settings.width !== 0
      );
      let nonAutoWidthSum = 0;
      filterNonAutoWidth.forEach((col: any) => {
        nonAutoWidthSum += col.settings.width;
      });
      const tableWidth = tableRef?.current?.getBoundingClientRect().width;
      if (tableWidth) {
        updateColumns.forEach((col: any) => {
          if (col.settings.width === 0) {
            col.width =
              (12 / filterAutoWidthLength / 12) *
              (tableWidth - (nonAutoWidthSum / 12) * tableWidth);
          } else {
            col.width = (col.settings.width / 12) * tableWidth;
          }
        });
      }
      setColumns(updateColumns);
    }
  };

  const handleAddRow = () => {
    const newId = rows.length + 1;
    const newRow: any = { id: newId };

    columns.forEach((col) => {
      newRow[col.field] = "";
    });

    setRows((prev) => [...prev, newRow]);
  };

  const onEditHeaderClick = (params: any) => {
    setIsEdit(true);
    setSelectedColumn(params.colDef.settings);
  };

  const handleAddColumn = () => {
    const newId = columns.length + 1;
    const field = `col${newId}`;

    const defaultSettings: ColumnSettingsType = {
      label: `New Column`,
      inputType: "OpenEnd",
      inputFormat: "",
      column: columns.length,
      isSum: false,
      width: 0,
    };

    const newColumn: GridColDef & { settings: ColumnSettingsType } = {
      field,
      headerName: defaultSettings.label,
      width: defaultSettings.width,
      type: "singleSelect",
      valueOptions: ["defined Name"],
      renderHeader: (params) => (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            width: params.colDef.width,
          }}
        >
          <Typography
            sx={{
              width: params?.colDef?.width ? params?.colDef?.width - 50 : 100,
              fontWeight: "600",
            }}
            noWrap
          >
            {params.colDef.headerName}
          </Typography>
          <IconButton onClick={() => onEditHeaderClick(params)}>
            <EditIcon fontSize="small" />
          </IconButton>
        </Box>
      ),
      renderCell: (params) => {
        if (params.id !== "sum") {
          if (params?.colDef?.type === "string") {
            return (
              <TextField
                fullWidth
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": {
                      border: "none",
                    },
                    "&:hover fieldset": {
                      border: "none",
                    },
                    "&.Mui-focused fieldset": {
                      border: "none",
                    },
                    "& .MuiOutlinedInput-input": {
                      padding: "13px 2px",
                    },
                  },
                }}
                value={params?.value || ""}
                onKeyDown={(e) => {
                  if (e.key === " ") {
                    e.stopPropagation();
                  }
                }}
                onChange={(event) => {
                  handleTextChange(
                    params.id as number,
                    params.field,
                    event.target.value
                  );
                }}
              />
            );
          }

          return (
            <Autocomplete
              fullWidth
              sx={{
                width: "100%",
                height: 40,
                my: 0.75,
                "& .MuiOutlinedInput-root": {
                  padding: 0,
                },
              }}
              disablePortal
              options={definedNames}
              value={params?.value ? `#${params?.value}` : ""}
              getOptionDisabled={(option) =>
                selectedDefinedNames.includes(option)
              }
              onChange={(event, newValue: any) => {
                handleSelectChange(
                  params.id as number,
                  params.field,
                  newValue,
                  params.value
                );
              }}
              renderInput={(params) => (
                <TextField
                  sx={{
                    "& .MuiOutlinedInput-input": {
                      color: "#2E7D32",
                    },
                    "& .MuiOutlinedInput-root": {
                      paddingRight: "44px !important",
                    },
                    "& .MuiOutlinedInput-root .MuiAutocomplete-endAdornment": {
                      right: "0px !important",
                    },
                  }}
                  name="definedName"
                  {...params}
                />
              )}
              renderOption={(props, option) => (
                <li
                  {...props}
                  style={{
                    width: 300,
                    whiteSpace: "normal",
                    overflowWrap: "break-word",
                    wordBreak: "break-all",
                  }}
                >
                  {option}
                </li>
              )}
              slots={{ popper: Popper }}
              slotProps={{
                popper: {
                  disablePortal: false,
                  sx: {
                    zIndex: 1500,
                    width: "auto",
                  },
                },
                paper: {
                  sx: {
                    width: 300,
                    backgroundColor: "#fafafa",
                  },
                },
              }}
            />
          );
        }
      },
      settings: defaultSettings,
    };
    setColumns((prev) => [...prev, newColumn]);
    updateColumnWidth(newColumn);

    setRows((prev) =>
      prev.map((row) => ({
        ...row,
        [field]: "",
      }))
    );
    setIsEdit(false);
    setSelectedColumn(defaultSettings);
  };

  const onDeleteColumn = (index: number) => {
    setColumns((prevColumn) =>
      prevColumn
        .filter((_, i) => i !== index)
        .map((item: any, index) => ({
          ...item,
          field: `col${index + 1}`,
          settings: { ...item?.settings, column: index },
        }))
    );
    rows.forEach((row) => {
      const { id, ...rest } = row;
      const keys = Object.keys(rest);
      delete row[keys[index]];
    });
    setSelectedColumn(null);
  };

  const handleDeleteRow = (index: number) => {
    setRows((prevRows) => prevRows.filter((_, i) => i !== index));
  };

  const handleTextChange = (id: number, field: string, value: any) => {
    setRows((rows) =>
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleSelectChange = (
    id: number,
    field: string,
    value: any,
    oldValue: string
  ) => {
    setSelectedDefinedNames((prev) => {
      let newDefinedNames = [...prev];

      if (!value && oldValue) {
        // Remove old value
        newDefinedNames = newDefinedNames.filter((name) => name !== oldValue);
      } else if (value && !oldValue) {
        // Add new value
        newDefinedNames.push(value);
      } else if (value && oldValue) {
        // Replace old value with new
        newDefinedNames = newDefinedNames.filter((name) => name !== oldValue);
        newDefinedNames.push(value);
      }

      return newDefinedNames;
    });

    setRows((rows) =>
      rows.map((row) => (row.id === id ? { ...row, [field]: value } : row))
    );
  };

  const handleSaveSettings = (column: any) => {
    columns.forEach((col, i) => {
      if (i === selectedColumn?.column) {
        updateColumnWidth({
          ...col,
          headerName: column.label,
          settings: { ...column },
        });
      }
    });
    setIsEdit(false);
    setSelectedColumn(null);
  };

  const onSaveTable = () => {
    if (columns.length === 0) {
      setErrorMessage("Atleast one column is required to save this table.");
      setShowErrorNotification(true);
      return;
    } else if (rows.length === 0) {
      setErrorMessage("Atleast one row is required to save this table.");
      setShowErrorNotification(true);
      return;
    }
    const childElements: any[] = [];
    columns.forEach((column: any) => {
      const element: any = {
        title: column?.settings?.label,
        tableElementSettings: {
          row: 0,
          column: column?.settings?.column,
          isHeader: true,
          headerSettings: {
            calculateSum: column?.settings?.isSum,
            width: column?.settings?.width,
            inputType: column?.settings?.inputType,
          },
        },
      };
      if (column?.settings?.inputType === "OpenEnd") {
        element.tableElementSettings.headerSettings.inputFormat =
          column?.settings?.inputFormat;
      }
      if (column?.settings?.inputType === "SingleSelect") {
        element.answerOptions = column?.settings?.answerOptions
          .split(",")
          .map((item: string) => item.trim())
          .filter(Boolean);
      }
      childElements.push(element);
    });

    rows.forEach((row: any, index: number) => {
      const { id, ...rest } = row;
      const keys = Object.keys(rest);
      keys.forEach((key, columnIndex) => {
        const element: any = {
          tableElementSettings: {
            row: index + 1,
            column: columnIndex,
            isHeader: false,
          },
        };
        const filterColumn: any = columns.find((col) => col.field === key);
        if (filterColumn?.settings?.inputType === "TextField") {
          element.title = row[key];
        } else {
          element.definedName = row[key];
        }
        childElements.push(element);
      });
    });

    if (checkDuplicate(childElements)) {
      //if duplicate defined name
      setErrorMessage("Duplicate Defined Names not allowed.");
      setShowErrorNotification(true);
    } else {
      onSave(childElements);
      closeDialog();
    }
  };

  const checkDuplicate = (childElements: any[]) => {
    const seen = new Set();
    for (const item of childElements) {
      const value = item?.definedName;
      if (seen.has(value)) {
        return true;
      }
      if (value) seen.add(value);
    }
    return false;
  };

  return (
    <Box>
      <Dialog
        open={showDialog}
        onClose={closeDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullWidth={true}
        maxWidth={"lg"}
      >
        <DialogTitle id="responsive-dialog-title">
          <Typography variant="h5" color="black">
            Configure Table
          </Typography>
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={closeDialog}
          sx={(theme) => ({
            position: "absolute",
            right: 8,
            top: 8,

            color: "black",
          })}
        >
          <CloseIcon />
        </IconButton>

        <DialogContent dividers>
          <DialogContentText id="alert-dialog-description">
            <Box sx={{ display: "flex" }}>
              <DataGrid
                ref={tableRef}
                rows={rows}
                columns={columns}
                isCellEditable={(params) => params.id !== "sum"}
                disableColumnResize
                disableColumnFilter
                disableColumnMenu
                disableColumnSorting
                hideFooter
                slots={{
                  noRowsOverlay: () => null,
                }}
                sx={{
                  width: "calc(100% - 56px)",
                  border: "none",
                  "& .MuiDataGrid-cell": {
                    border: "1px solid rgba(224,224,224,1)",
                  },
                  "& .MuiDataGrid-columnHeader": {
                    border: "1px solid rgba(224,224,224,1)",
                  },
                  "& .MuiDataGrid-columnHeaders": {
                    borderBottom: "none",
                  },
                  "& .MuiDataGrid-cellEmpty": {
                    all: "unset",
                    display: "none",
                  },
                  "& .MuiDataGrid-filler": {
                    all: "unset",
                  },
                  "& .MuiDataGrid-row:hover": {
                    backgroundColor: "transparent",
                  },
                  "& .MuiDataGrid-row.Mui-selected": {
                    backgroundColor: "transparent",
                  },
                  "& .MuiDataGrid-row.Mui-selected:hover": {
                    backgroundColor: "transparent",
                  },
                }}
              />
              <Box>
                <IconButton
                  sx={{
                    backgroundColor: "rgb(227,95,0)",
                    color: "white",
                    "&:hover": {
                      backgroundColor: "rgb(200,80,0)",
                    },
                    m: 1,
                  }}
                  onClick={handleAddColumn}
                  disabled={allColumnsWidth >= 12}
                >
                  <AddOutlinedIcon />
                </IconButton>
                {rows.map((row, index) => (
                  <Box
                    key={row.id ?? index}
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      height: 52,
                    }}
                  >
                    <IconButton onClick={() => handleDeleteRow(index)}>
                      <DeleteOutlineOutlinedIcon color="error" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            </Box>

            <IconButton
              sx={{
                backgroundColor: "rgb(227,95,0)",
                color: "white",
                "&:hover": {
                  backgroundColor: "rgb(200,80,0)",
                },
                m: 1,
              }}
              onClick={handleAddRow}
              disabled={!(columns.length > 0)}
            >
              <AddOutlinedIcon />
            </IconButton>

            {selectedColumn && (
              <ColumnSettings
                columnData={selectedColumn}
                isEdit={isEdit}
                onSave={handleSaveSettings}
                onCancel={() => setSelectedColumn(null)}
                onDelete={onDeleteColumn}
                totalWidth={allColumnsWidth}
              />
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => {
              const childElementsCount =
                columns.length + columns.length * rows.length;
              if (tableData.length === childElementsCount) {
                closeDialog();
              } else {
                setShowAlertDialog(true);
              }
            }}
            disabled={selectedColumn}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={onSaveTable}
            disabled={selectedColumn}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
      {showErrorNotification && (
        <ErrorNotification
          show={showErrorNotification}
          onClose={() => setShowErrorNotification(false)}
          title={errorMessage}
        />
      )}
      {showAlertDialog && (
        <ConfirmationDialog
          show={showAlertDialog}
          close={() => setShowAlertDialog(false)}
        />
      )}
    </Box>
  );
}
